/**
 * Guest Requests context — the front desk's live work queue state.
 *
 * Keeps the merged request list fresh three ways:
 *  1. 60s polling (always-on fallback)
 *  2. WebSocket guest_request_event pushes → debounced refetch
 *  3. refetch after every staff action (acknowledge / complete)
 *
 * New-request arrivals raise a toast and a short chime (mutable,
 * persisted) so orders don't sit unseen at a busy desk.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useToast } from '@chakra-ui/react';
import guestRequestsService from '../service/GuestRequestsService';
import webSocketService from '../service/WebSocketService';

const POLL_INTERVAL_MS = 60 * 1000;
const WS_REFRESH_DEBOUNCE_MS = 800;
const MUTE_STORAGE_KEY = 'guestRequestsMuted';

const GuestRequestsContext = createContext(null);

export const useGuestRequests = () => useContext(GuestRequestsContext);

/** Two-tone chime via WebAudio — no asset file needed. */
const playChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [[880, 0], [1174.66, 0.18]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.35);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {
    // Audio is best-effort (autoplay policies etc.)
  }
};

export const GuestRequestsProvider = ({ children }) => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, done_today: 0 });
  const [loading, setLoading] = useState(true);
  const [muted, setMutedState] = useState(
    () => localStorage.getItem(MUTE_STORAGE_KEY) === 'true'
  );
  const toast = useToast();
  const wsRefreshTimer = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const refresh = useCallback(async () => {
    try {
      const data = await guestRequestsService.getRequests();
      // Name the shape mismatch instead of letting a TypeError in the
      // catch below look like a network failure — that misread cost a
      // day when the service double-unwrapped the response.
      if (!data || !Array.isArray(data.requests)) {
        console.warn(
          'Guest requests: unexpected response shape (expected {requests,counts}), got:',
          data
        );
        return;
      }
      setRequests(data.requests);
      setCounts(data.counts || { new: 0, in_progress: 0, done_today: 0 });
    } catch (error) {
      console.warn('Guest requests refresh failed:', error?.message || error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll as the always-on fallback — only while the tab is visible
  // (background tabs catch up once on return; WS pushes still arrive).
  useEffect(() => {
    refresh();
    const interval = setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Real-time: refetch (debounced) on every push; chime + toast on new
  useEffect(() => {
    const handleEvent = (data) => {
      if (wsRefreshTimer.current) clearTimeout(wsRefreshTimer.current);
      wsRefreshTimer.current = setTimeout(refresh, WS_REFRESH_DEBOUNCE_MS);

      const event = data?.event || '';
      if (event.endsWith('_created')) {
        const request = data?.request || {};
        const isOrder = request.kind === 'room_service_order';
        toast({
          title: isOrder ? 'New room service order' : 'New guest request',
          description: [
            request.room_number ? `Room ${request.room_number}` : null,
            request.guest_name || null,
            request.summary || null,
          ].filter(Boolean).join(' · '),
          status: 'info',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
        if (!mutedRef.current) playChime();
      }
    };

    webSocketService.on('guest_request_event', handleEvent);
    return () => {
      webSocketService.off('guest_request_event', handleEvent);
      if (wsRefreshTimer.current) clearTimeout(wsRefreshTimer.current);
    };
  }, [refresh, toast]);

  const setMuted = useCallback((value) => {
    setMutedState(value);
    localStorage.setItem(MUTE_STORAGE_KEY, value ? 'true' : 'false');
  }, []);

  const acknowledge = useCallback(async (item) => {
    await guestRequestsService.acknowledge(item);
    await refresh();
  }, [refresh]);

  const complete = useCallback(async (item) => {
    await guestRequestsService.complete(item);
    await refresh();
  }, [refresh]);

  const value = {
    requests,
    counts,
    loading,
    muted,
    setMuted,
    refresh,
    acknowledge,
    complete,
  };

  return (
    <GuestRequestsContext.Provider value={value}>
      {children}
    </GuestRequestsContext.Provider>
  );
};

export default GuestRequestsContext;
